import { useContext, useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { History } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { SESSION_STORAGE_KEYS } from '@/lib/constants/sessionStorageKeys';
import { usePermissionsStore } from '@/lib/stores/usePermissionsStore';
import { formatCurrencyWithPlaceholder } from '@/lib/utils/currency';
import { formatDate } from '@/lib/utils/dates';

import { glacierPolicyApiGetLatestTransactionLatestAttemptOptions } from '@/lib/heyapi/@tanstack/react-query.gen';
import type {
  ChildPolicyData,
  Policy,
  TransactionSummaryData,
} from '@/lib/heyapi/types.gen';
import { TransactionStatus } from '@/lib/heyapi/types.gen';

import { GlobalLoadingContext } from '../../GlobalLoadingContext';
import { getCleanText, TEXT_PLACEHOLDER } from '../utils';
import { DebugTransaction } from './DropdownComponents/DebugTransaction';
import { DropdownMenuButton } from './DropdownMenuButton';
import { StandardMessageHeader } from './StandardMessage';

type RatingMetadata = {
  proration_str: string;
};

type RatingResponse = {
  metadata: RatingMetadata;
};

export const HeaderView = ({
  renewalPolicyData,
  policy,
  latestTransactionSummary,
  hasAlerts = false,
}: {
  renewalPolicyData: ChildPolicyData | null;
  policy: Policy;
  latestTransactionSummary: TransactionSummaryData | undefined;
  hasAlerts?: boolean;
}) => {
  const { headerMessage: message, setHeaderMessage: setMessage } =
    useContext(GlobalLoadingContext);
  const isTimeTraveling = usePermissionsStore((state) => state.isTimeTraveling);
  const [openDebugDialog, setOpenDebugDialog] = useState(false);
  const [loadingDebug, setLoadingDebug] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  const { data: latestRatingAttempt } = useQuery({
    ...glacierPolicyApiGetLatestTransactionLatestAttemptOptions({
      path: {
        account_id: policy.pk!,
      },
    }),
  });

  const effectiveDate = useMemo(() => {
    return formatDate(policy?.details?.policyEffectiveDate) || TEXT_PLACEHOLDER;
  }, [policy?.details?.policyEffectiveDate]);

  const transactionEffectiveDate = useMemo(() => {
    return (
      formatDate(policy?.latestTransaction?.effectiveDate) || TEXT_PLACEHOLDER
    );
  }, [policy?.latestTransaction?.effectiveDate]);

  const expirationDate = useMemo(
    () => formatDate(policy?.details?.policyExpirationDate) || TEXT_PLACEHOLDER,
    [policy?.details?.policyExpirationDate]
  );

  const firstNamedInsured = policy.firstNamedInsured;

  const assignedUser = useMemo(() => {
    return policy?.assignedTo?.firstName
      ? `${policy.assignedTo.firstName} ${policy.assignedTo.lastName}`
      : TEXT_PLACEHOLDER;
  }, [policy?.assignedTo?.firstName, policy?.assignedTo?.lastName]);

  const referredBy = useMemo(() => {
    return policy?.referredBy?.firstName
      ? `${policy.referredBy.firstName} ${policy.referredBy.lastName}`
      : null;
  }, [policy?.referredBy?.firstName, policy?.referredBy?.lastName]);

  // Use backend-computed label directly
  const statusStage = useMemo(() => {
    return policy?.policyStatusLabel || TEXT_PLACEHOLDER;
  }, [policy?.policyStatusLabel]);

  const prorationFactor = useMemo(() => {
    const response = latestRatingAttempt?.response as RatingResponse;
    return response?.metadata?.proration_str
      ? response.metadata?.proration_str
      : TEXT_PLACEHOLDER;
  }, [latestRatingAttempt?.response]);

  const getBgColor = useMemo(() => {
    if (isTimeTraveling) {
      return 'bg-[#D4ECEE]';
    }
    if (hasAlerts) {
      return 'bg-yellow-200';
    }
    return 'bg-brand-100';
  }, [isTimeTraveling, hasAlerts]);

  return (
    <header className="bg-base-100/80 !text-[14px]">
      {/* backdrop-blur-md */}
      <section className="sm:text-xs 2xl:text-[14px]">
        <div
          className={`pointer-events-auto w-full overflow-hidden border-brand-200 ${getBgColor}`}
        >
          {/* Grid layout: 4 */}
          <div className="grid grid-cols-[1fr_auto_1fr_auto_1fr_auto_auto] items-stretch">
            {/* Column 1: Insured Info */}
            <div className="flex min-w-0 flex-col gap-1 py-4 pl-8 pr-4">
              <div className="flex gap-4 text-sm">
                <span className="w-[200px] shrink-0">First Name Insured:</span>
                <span
                  className="min-w-0 flex-1 truncate font-bold"
                  title={firstNamedInsured}
                >
                  {getCleanText(firstNamedInsured)}
                </span>
              </div>
              <div className="flex gap-4 text-sm">
                <span className="w-[200px] shrink-0">Agency:</span>
                <span
                  className="min-w-0 flex-1 truncate font-bold"
                  title={policy?.details?.agencyName || 'agencyName'}
                >
                  {getCleanText(policy?.details?.agencyName)}
                </span>
              </div>
              <div className="flex gap-4 text-sm">
                <span className="w-[200px] shrink-0">
                  {referredBy ? 'Assigned To/Referred By:' : 'Assigned To:'}
                </span>
                <span
                  className="min-w-0 flex-1 truncate font-bold"
                  title={
                    referredBy
                      ? `${assignedUser} / ${referredBy}`
                      : assignedUser
                  }
                >
                  {referredBy
                    ? `${getCleanText(assignedUser)} / ${getCleanText(referredBy)}`
                    : getCleanText(assignedUser)}
                </span>
              </div>
              <div className="flex gap-4 text-sm">
                <span className="w-[200px] shrink-0">
                  Change Effective Date:
                </span>
                <span
                  className="min-w-0 flex-1 truncate font-bold"
                  title={transactionEffectiveDate || ''}
                >
                  {getCleanText(transactionEffectiveDate)}
                </span>
              </div>
              <div className="flex gap-4 text-sm">
                <span className="w-[200px] shrink-0">Proration:</span>
                <span
                  className="min-w-0 flex-1 truncate font-bold"
                  title={prorationFactor || ''}
                >
                  {getCleanText(prorationFactor)}
                </span>
              </div>
            </div>

            <Separator
              className="mx-[10px] my-4 h-auto"
              orientation="vertical"
            />

            {/* Column 2: Quote/Policy Info */}
            <div className="flex min-w-0 flex-col gap-1 px-4 py-4">
              <div className="flex gap-4 text-sm">
                <span className="w-[200px] shrink-0">Quote Number:</span>
                <span
                  className="min-w-0 flex-1 truncate font-bold"
                  title={policy?.policyNumber || ''}
                >
                  {getCleanText(policy?.policyNumber)}
                </span>
              </div>
              <div className="flex gap-4 text-sm">
                <span className="w-[200px] shrink-0">
                  Primary Practice State:
                </span>
                <span
                  className="min-w-0 flex-1 truncate font-bold"
                  title={policy?.details?.policyContractState || ''}
                >
                  {getCleanText(policy?.details?.policyContractState)}
                </span>
              </div>
              <div className="flex gap-4 text-sm">
                <span className="w-[200px] shrink-0">Stage/Status:</span>
                <span
                  className="min-w-0 flex-1 truncate font-bold"
                  title={statusStage}
                >
                  {getCleanText(statusStage)}
                </span>
              </div>
              <div className="flex gap-4 text-sm">
                <span className="w-[200px] shrink-0">Policy Period</span>
                <span
                  className="min-w-0 flex-1 truncate font-bold"
                  title={`${effectiveDate} - ${expirationDate}`}
                >
                  {effectiveDate} - {expirationDate}
                </span>
              </div>
              <div className="flex gap-4 text-sm">
                <span className="w-[200px] shrink-0">Transaction Type</span>
                <span
                  className="min-w-0 flex-1 truncate font-bold"
                  title={policy?.transactionTypeLabel || ''}
                >
                  {policy?.transactionTypeLabel || TEXT_PLACEHOLDER}
                </span>
              </div>
            </div>

            <Separator
              className="mx-[10px] my-4 h-auto"
              orientation="vertical"
            />

            {/* Column 3: Policy Numbers & Amounts */}
            <div className="flex min-w-0 flex-col gap-1 px-4 py-4">
              <div className="flex gap-4 text-sm">
                <span className="w-[200px] shrink-0">Policy Number:</span>
                <span
                  className="flex min-w-0 flex-1 items-center truncate font-bold"
                  title={policy?.boundPolicyNumber || ''}
                >
                  {getCleanText(policy?.boundPolicyNumber)}
                  {policy &&
                  policy?.latestTransaction.status ===
                    TransactionStatus.ACTIVE ? (
                    <span className="ml-2 shrink-0" title="Locked">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 448 512"
                        width={15}
                      >
                        <path d="M144 144l0 48 160 0 0-48c0-44.2-35.8-80-80-80s-80 35.8-80 80zM80 192l0-48C80 64.5 144.5 0 224 0s144 64.5 144 144l0 48 16 0c35.3 0 64 28.7 64 64l0 192c0 35.3-28.7 64-64 64L64 512c-35.3 0-64-28.7-64-64L0 256c0-35.3 28.7-64 64-64l16 0z"></path>
                      </svg>
                    </span>
                  ) : null}
                </span>
              </div>
              <div className="flex gap-4 text-sm">
                <span className="w-[200px] shrink-0">Transaction ID:</span>
                <span
                  className="min-w-0 flex-1 truncate font-bold"
                  title="Transaction ID"
                >
                  {getCleanText(policy?.transactionId)}
                </span>
              </div>
              <div className="flex gap-4 text-sm">
                <span className="w-[200px] shrink-0">Written Amount:</span>
                <span
                  className="min-w-0 flex-1 truncate font-bold"
                  title="Written Premium"
                >
                  {formatCurrencyWithPlaceholder(
                    (policy?.latestTransaction?.writtenPremium || 0) +
                      (policy?.latestTransaction?.writtenTax || 0)
                  )}
                </span>
              </div>
              <div className="flex gap-4 text-sm">
                <span className="w-[200px] shrink-0">Billed Amount:</span>
                <span
                  className="min-w-0 flex-1 truncate font-bold"
                  title="Billed Amount"
                >
                  {formatCurrencyWithPlaceholder(
                    (policy?.latestTransaction?.billedPremium || 0) +
                      (policy?.latestTransaction?.billedTax || 0)
                  )}
                </span>
              </div>
              <div className="flex gap-4 text-sm">
                <span className="w-[200px] shrink-0">Annualized Amount:</span>
                <span
                  className="min-w-0 flex-1 truncate font-bold"
                  title="Annualized Amount"
                >
                  {formatCurrencyWithPlaceholder(
                    (policy?.latestTransaction?.annualPremium || 0) +
                      (policy?.latestTransaction?.annualTax || 0)
                  )}
                </span>
              </div>
            </div>

            <Separator
              className="mx-[10px] my-4 h-auto"
              orientation="vertical"
            />

            {/* Column 4: Actions (3-dots menu or Time Travel buttons) */}
            {isTimeTraveling ? (
              <div className="flex flex-col items-center justify-center gap-2 px-4 py-4">
                <Button
                  variant="outline"
                  size="sm"
                  className="whitespace-nowrap"
                  onClick={() => {
                    sessionStorage.removeItem(SESSION_STORAGE_KEYS.SEQUENCE_ID);
                    window.location.reload();
                  }}
                >
                  <History size={20} strokeWidth={1.5} className="rounded-sm" />
                  End Time Travel
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="whitespace-nowrap"
                  disabled={loadingDebug}
                  onClick={() => {
                    setOpenDebugDialog(true);
                    setLoadingDebug(true);
                  }}
                >
                  Debug Previous Rating
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-center px-4 py-4 pr-8">
                <DropdownMenuButton
                  renewalPolicyData={renewalPolicyData}
                  policy={policy}
                  setMessage={setMessage}
                  latestTransactionSummary={latestTransactionSummary}
                  latestRateAttempt={latestRatingAttempt}
                />
              </div>
            )}
          </div>
        </div>
        <StandardMessageHeader message={message} />
        {submissionError ? (
          <div className="fixed inset-0 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-2xl rounded-lg bg-white p-6">
              <h2 className="mb-4 text-xl font-bold text-red-600">
                Policy Issuance Failed
              </h2>
              {submissionError}
              <button
                type="button"
                className="mt-4 rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700"
                onClick={() => setSubmissionError(null)}
              >
                Close
              </button>
            </div>
          </div>
        ) : null}
      </section>
      <Dialog open={openDebugDialog} onOpenChange={setOpenDebugDialog}>
        <DialogContent className="w-full max-w-[80vw] overflow-auto [&>button]:right-2 [&>button]:top-2">
          <DebugTransaction
            accountId={policy.pk!}
            setLoadingDebug={setLoadingDebug}
          />
        </DialogContent>
      </Dialog>
    </header>
  );
};
